import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, User, Bot, ChevronDown, ChevronRight, Brain, X, FileText, Copy, Download, Code, GitCompare, Eye, AlertCircle, MessageCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { webSocketClient, sendFindingChatMessage, handleStreamingResponse, cancelChatMessage, YAMLFix, ThinkingStep } from '@/api/websocket';
import { useAuth } from '@/hooks/useAuth';
import { useChatSessionManager } from '@/hooks/useChatSessionManager';
import { ChatHistoryDropdown } from '@/components/chat/ChatHistoryDropdown';
import { RiskFinding } from '@/api/client';
import { ThinkingDisplay } from '@/components/chat/ThinkingStepItem';
import { ChatMarkdown } from '@/components/chat/MarkdownRenderer';
import { createDiff, ChatMessage } from '@/components/chat/chat-utils';

interface FindingChatProps {
  finding: RiskFinding;
  assessmentId: string;
}

// Component for displaying YAML fixes
interface YAMLFixesDisplayProps {
  yamlFixes: YAMLFix[];
}

function YAMLFixesDisplay({ yamlFixes }: YAMLFixesDisplayProps) {
  const [expandedFixes, setExpandedFixes] = useState<Set<number>>(new Set());
  const [showDiffView, setShowDiffView] = useState<Set<number>>(new Set());
  const [copiedFix, setCopiedFix] = useState<number | null>(null);

  const toggleFixExpansion = (fixIndex: number) => {
    setExpandedFixes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fixIndex)) {
        newSet.delete(fixIndex);
      } else {
        newSet.add(fixIndex);
      }
      return newSet;
    });
  };

  const toggleDiffView = (fixIndex: number) => {
    setShowDiffView(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fixIndex)) {
        newSet.delete(fixIndex);
      } else {
        newSet.add(fixIndex);
      }
      return newSet;
    });
  };

  const copyYamlToClipboard = async (yamlContent: string, fixIndex: number) => {
    try {
      await navigator.clipboard.writeText(yamlContent);
      setCopiedFix(fixIndex);
      setTimeout(() => setCopiedFix(null), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const downloadYamlFile = (yamlContent: string, filePath: string) => {
    const blob = new Blob([yamlContent], { type: 'text/yaml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    const filename = filePath.split('/').pop() || 'fix.yaml';
    link.setAttribute('download', filename);
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getFileTypeBadge = (fileType: string) => {
    switch (fileType) {
      case 'create': return <Badge className="bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800">Create</Badge>;
      case 'update': return <Badge className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800">Update</Badge>;
      case 'delete': return <Badge className="bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">Delete</Badge>;
      default: return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  const getFileTypeIcon = (fileType: string) => {
    switch (fileType) {
      case 'create': return <FileText className="h-4 w-4 text-green-600" />;
      case 'update': return <FileText className="h-4 w-4 text-blue-600" />;
      case 'delete': return <FileText className="h-4 w-4 text-red-600" />;
      default: return <FileText className="h-4 w-4 text-gray-600" />;
    }
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex items-center gap-3">
        <div className="w-px h-6 bg-gradient-to-b from-green-500 to-blue-500"></div>
        <h4 className="text-sm font-semibold text-foreground">Remediation Steps</h4>
        <Badge variant="outline" className="text-xs">
          {yamlFixes.length} {yamlFixes.length === 1 ? 'fix' : 'fixes'}
        </Badge>
      </div>

      {yamlFixes.map((fix, index) => {
        const isExpanded = expandedFixes.has(index);
        const isCopied = copiedFix === index;

        return (
          <Card key={index} className="border-l-4 border-l-blue-500">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {getFileTypeIcon(fix.file_type)}
                  <div>
                    <div className="text-sm font-medium">{fix.file_path}</div>
                    <div className="flex items-center gap-2 mt-1">
                      {getFileTypeBadge(fix.file_type)}
                      {fix.resource_name && (
                        <span className="text-xs text-muted-foreground">
                          {fix.resource_kind}/{fix.resource_name}
                          {fix.resource_namespace && ` in ${fix.resource_namespace}`}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyYamlToClipboard(fix.yaml_content, index)}
                    className="text-xs"
                  >
                    <Copy className="h-3 w-3 mr-1" />
                    {isCopied ? 'Copied!' : 'Copy'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadYamlFile(fix.yaml_content, fix.file_path)}
                    className="text-xs"
                  >
                    <Download className="h-3 w-3 mr-1" />
                    Download
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {fix.explanation && (
                <div>
                  <h5 className="text-xs font-medium text-foreground mb-2">What this fixes:</h5>
                  <p className="text-xs text-muted-foreground">{fix.explanation}</p>
                </div>
              )}

              <Collapsible open={isExpanded} onOpenChange={() => toggleFixExpansion(index)}>
                <CollapsibleTrigger asChild>
                  <Button variant="outline" size="sm" className="w-full justify-between text-xs">
                    <span className="flex items-center gap-2">
                      <Code className="h-3 w-3" />
                      View YAML Configuration
                    </span>
                    {isExpanded ? (
                      <ChevronDown className="h-3 w-3" />
                    ) : (
                      <ChevronRight className="h-3 w-3" />
                    )}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-3">
                  <div className="space-y-3">
                    {/* Toggle between single view and diff view for updates */}
                    {fix.existing_yaml && fix.file_type === 'update' && (
                      <div className="flex justify-center">
                        <div className="flex items-center gap-2 bg-muted p-1 rounded-md">
                          <Button
                            variant={!showDiffView.has(index) ? "default" : "ghost"}
                            size="sm"
                            onClick={() => setShowDiffView(prev => {
                              const newSet = new Set(prev);
                              newSet.delete(index);
                              return newSet;
                            })}
                            className="text-xs h-6"
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            New YAML
                          </Button>
                          <Button
                            variant={showDiffView.has(index) ? "default" : "ghost"}
                            size="sm"
                            onClick={() => toggleDiffView(index)}
                            className="text-xs h-6"
                          >
                            <GitCompare className="h-3 w-3 mr-1" />
                            Show Diff
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {/* YAML Content Display */}
                    {fix.existing_yaml && fix.file_type === 'update' && showDiffView.has(index) ? (
                      // Unified diff view for updates
                      <div className="bg-muted p-3 rounded-md border font-mono text-xs overflow-x-auto max-h-64">
                        {createDiff(fix.existing_yaml, fix.yaml_content).map((diffLine, lineIndex) => (
                          <div 
                            key={lineIndex} 
                            className={cn(
                              "whitespace-pre-wrap flex",
                              diffLine.type === 'removed' && "bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200",
                              diffLine.type === 'added' && "bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200",
                              diffLine.type === 'unchanged' && "text-foreground hover:bg-muted/50"
                            )}
                          >
                            <span className={cn(
                              "inline-block w-6 text-right mr-2 text-muted-foreground select-none flex-shrink-0 border-r border-border/50 pr-1",
                              diffLine.type === 'removed' && "text-red-600 dark:text-red-400 bg-red-100/50 dark:bg-red-900/30",
                              diffLine.type === 'added' && "text-green-600 dark:text-green-400 bg-green-100/50 dark:bg-green-900/30"
                            )}>
                              {diffLine.type === 'removed' ? '-' : diffLine.type === 'added' ? '+' : ' '}
                            </span>
                            <span className="flex-1 min-w-0">
                              {diffLine.line}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      // Single view (for creates, deletes, or when not in diff mode)
                      <pre className={cn(
                        "p-3 rounded-md border font-mono text-xs overflow-x-auto max-h-64",
                        fix.file_type === 'delete' 
                          ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800"
                          : fix.file_type === 'create'
                          ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
                          : "bg-muted"
                      )}>
                        <code className="text-foreground">
                          {fix.file_type === 'delete' && fix.existing_yaml 
                            ? fix.existing_yaml 
                            : fix.yaml_content}
                        </code>
                      </pre>
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

export function FindingChat({ finding, assessmentId }: FindingChatProps) {
  const { isAuthenticated } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [currentBotMessage, setCurrentBotMessage] = useState<string>('');
  const [isTyping, setIsTyping] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  
  // Thinking state management
  const [isThinking, setIsThinking] = useState(false);
  const [thinkingText, setThinkingText] = useState<string>('');
  const [thinkingSteps, setThinkingSteps] = useState<ThinkingStep[]>([]);
  const [isThinkingExpanded, setIsThinkingExpanded] = useState(true); // Default to expanded
  const [thinkingComplete, setThinkingComplete] = useState(false);
  
  // Cancellation state management
  const [currentMessageId, setCurrentMessageId] = useState<string | null>(null);
  const [canCancel, setCanCancel] = useState(false);
  
  // YAML fixes state management
  const [currentYAMLFixes, setCurrentYAMLFixes] = useState<YAMLFix[] | null>(null);
  const [waitingForFixes, setWaitingForFixes] = useState(false);
  const waitingForFixesTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Chat history - gate on auth so session bootstrap doesn't run before WebSocket is ready
  const sessionManager = useChatSessionManager(
    { contextType: 'finding', contextId: finding.id, enabled: !!isAuthenticated },
    setMessages
  );

  // Auto-scroll with user scroll-up detection
  const userWantsToStayUpRef = useRef(false);
  const scrollToBottom = useCallback(() => {
    if (userWantsToStayUpRef.current) return;
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    const el = scrollAreaRef.current;
    if (!el) return;
    const markUp = () => { userWantsToStayUpRef.current = true; };
    const checkBottom = () => {
      const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (dist < 50) userWantsToStayUpRef.current = false;
    };
    const onWheel = (e: WheelEvent) => { if (e.deltaY < 0) markUp(); else setTimeout(checkBottom, 50); };
    el.addEventListener('wheel', onWheel, { passive: true });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, currentBotMessage, isThinking, thinkingText, scrollToBottom]);

  useEffect(() => {
    let isComponentMounted = true;

    // Only initialize WebSocket if user is authenticated
    if (!isAuthenticated) {
      return;
    }

    // Initialize WebSocket connection
    const initializeWebSocket = async () => {
      try {
        setConnectionError(null);
        await webSocketClient.connect();
        if (isComponentMounted) {
          setIsConnected(true);
        }
      } catch (error) {
        if (isComponentMounted) {
          console.error('Failed to connect to WebSocket:', error);
          setConnectionError('Failed to connect to chat service. Please try refreshing the page.');
          setIsConnected(false);
        }
      }
    };

    // Set up WebSocket event listeners
    const handleConnect = () => {
      if (isComponentMounted) {
        setIsConnected(true);
        setConnectionError(null);
      }
    };

    const handleDisconnect = () => {
      if (isComponentMounted) {
        setIsConnected(false);
        setConnectionError('Connection lost. Attempting to reconnect...');
      }
    };

    const handleError = (error: Event) => {
      if (isComponentMounted) {
        console.error('WebSocket error:', error);
        setConnectionError('Connection error occurred.');
      }
    };

    webSocketClient.onConnect(handleConnect);
    webSocketClient.onDisconnect(handleDisconnect);
    webSocketClient.onError(handleError);

    // Start connection
    initializeWebSocket();

    // Cleanup on unmount
    return () => {
      isComponentMounted = false;
    };
  }, [isAuthenticated]);

  const getSamplePrompts = () => [
    "How do I fix this vulnerability?",
    "Show me the exact YAML changes needed",
    "What's the impact if I don't fix this?",
    "Are there any prerequisites for this fix?"
  ];

  const handleSamplePrompt = (prompt: string) => {
    setInput(prompt);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + 'px';
      }
    }, 50);
  };

  const handleCancel = () => {
    if (currentMessageId && canCancel) {
      cancelChatMessage(currentMessageId);

      // Preserve whatever has been streamed so far
      const partialText = currentBotMessage;
      const partialYAMLFixes = currentYAMLFixes;
      const partialThinking = thinkingText;
      const partialSteps = thinkingSteps;

      setIsLoading(false);
      setIsTyping(false);
      setIsThinking(false);
      setThinkingComplete(false);
      setThinkingText('');
      setThinkingSteps([]);
      setCurrentBotMessage('');
      setCurrentYAMLFixes(null);
      if (waitingForFixesTimerRef.current) clearTimeout(waitingForFixesTimerRef.current);
      setWaitingForFixes(false);
      setCanCancel(false);
      setCurrentMessageId(null);

      setMessages((prev) => [...prev, {
        id: `cancelled-${Date.now()}`,
        text: partialText || 'Request cancelled.',
        sender: 'bot',
        timestamp: Date.now(),
        thinking: partialThinking ? {
          text: partialThinking,
          steps: partialSteps,
          isExpanded: false,
        } : undefined,
        yamlFixes: partialYAMLFixes || undefined,
      }]);
    }
  };

  const handleSend = async () => {
    if (input.trim() === '' || !isConnected || isLoading) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      text: input.trim(),
      sender: 'user',
      timestamp: Date.now(),
    };
    
    setMessages((prev) => [...prev, userMessage]);
    const userInput = input.trim();
    setInput('');
    setIsLoading(true);
    setIsTyping(false);
    setCurrentBotMessage('');
    
    // Reset textarea height
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = '44px';
    }
    
    // Reset thinking state for new message
    setIsThinking(false);
    setThinkingText('');
    setThinkingSteps([]);
    setThinkingComplete(false);
    setIsThinkingExpanded(true); // Default to expanded during streaming

    setTimeout(() => {
      inputRef.current?.focus();
    }, 50);

    // Set up streaming response handler variables
    let accumulatedMessage = '';
    let accumulatedThinking = '';
    let accumulatedSteps: ThinkingStep[] = [];
    let receivedYAMLFixes: YAMLFix[] | null = null;

    try {
      // Send message via WebSocket with finding context
      const messageId = sendFindingChatMessage(
        userInput,
        finding.id,
        assessmentId,
        finding,
        finding.affected_resources || []
      );
      setCurrentMessageId(messageId);
      setCanCancel(true);
      
      const botMessageId = `bot-${Date.now()}`;
      let botMessageComplete = false;
      
      // Set up streaming response handler
      const handleResponse = handleStreamingResponse(
        messageId,
        // onChunk - handle answer chunks
        (chunk: string) => {
          accumulatedMessage += chunk;
          setCurrentBotMessage(accumulatedMessage);
          setIsTyping(false);
        },
        // onComplete - answer complete
        () => {
          if (waitingForFixesTimerRef.current) clearTimeout(waitingForFixesTimerRef.current);
          setWaitingForFixes(false);
          setMessages((prev) => [...prev, {
            id: botMessageId,
            text: accumulatedMessage,
            sender: 'bot',
            timestamp: Date.now(),
            thinking: (accumulatedThinking || accumulatedSteps.length > 0) ? {
              text: accumulatedThinking,
              steps: accumulatedSteps,
              isExpanded: false, // Collapse thinking after completion
            } : undefined,
            yamlFixes: receivedYAMLFixes || undefined,
          }]);
          setCurrentBotMessage('');
          setCurrentYAMLFixes(null);
          setIsLoading(false);
          setIsTyping(false);
          setIsThinking(false);
          setThinkingComplete(false);
          setThinkingText('');
          setThinkingSteps([]);
          setCanCancel(false);
          setCurrentMessageId(null);
          botMessageComplete = true;
          // Clean up the message handler to prevent memory leaks
          webSocketClient.offMessage(handleResponse);
        },
        // onThinking - handle thinking chunks with optional structured steps
        (thinkingChunk: string, steps?: ThinkingStep[]) => {
          accumulatedThinking += thinkingChunk;
          setThinkingText(accumulatedThinking);
          // Accumulate structured steps if provided, upsert by step id to allow status updates
          if (steps && steps.length > 0) {
            const existingStepsMap = new Map(accumulatedSteps.map(s => [s.id, s]));
            let hasChanges = false;

            for (const incomingStep of steps) {
              const existing = existingStepsMap.get(incomingStep.id);
              if (existing) {
                // Merge: preserve existing fields, update with incoming fields
                const merged = { ...existing, ...incomingStep };
                if (JSON.stringify(merged) !== JSON.stringify(existing)) {
                  existingStepsMap.set(incomingStep.id, merged);
                  hasChanges = true;
                }
              } else {
                // New step - append
                existingStepsMap.set(incomingStep.id, incomingStep);
                hasChanges = true;
              }
            }

            if (hasChanges) {
              accumulatedSteps = Array.from(existingStepsMap.values());
              setThinkingSteps(accumulatedSteps);
            }
          }
          setIsThinking(true);
          setIsTyping(false);
        },
        // onThinkingComplete - thinking phase complete, start answer
        () => {
          setThinkingComplete(true);
          setIsTyping(true);
        },
        // onCancelled - server acknowledged cancellation; handleCancel already
        // preserved partial text and reset UI, so just clean up the handler.
        () => {
          botMessageComplete = true;
          webSocketClient.offMessage(handleResponse);
        },
        // onYAMLFixes - handle YAML fixes (convert from cluster format to single format)
        (fixes: Record<string, YAMLFix[]>) => {
          const allFixes = Object.values(fixes).flat();
          receivedYAMLFixes = allFixes;
          setCurrentYAMLFixes(allFixes);
          if (waitingForFixesTimerRef.current) clearTimeout(waitingForFixesTimerRef.current);
          setWaitingForFixes(false);
        },
        // onError
        undefined,
        // onCloudFixes
        undefined,
        // onTextComplete - text streaming done, fixes may still be loading
        () => {
          const timer = setTimeout(() => setWaitingForFixes(true), 1000);
          waitingForFixesTimerRef.current = timer;
        }
      );

      // Listen for WebSocket messages
      webSocketClient.onMessage(handleResponse);

      // Set a timeout in case the response never comes
      setTimeout(() => {
        if (!botMessageComplete) {
          setMessages((prev) => [...prev, {
            id: botMessageId,
            text: 'Sorry, I encountered an error processing your request. Please try again.',
            sender: 'bot',
            timestamp: Date.now(),
            thinking: (accumulatedThinking || accumulatedSteps.length > 0) ? {
              text: accumulatedThinking,
              steps: accumulatedSteps,
              isExpanded: false,
            } : undefined,
            yamlFixes: receivedYAMLFixes || undefined,
          }]);
          setCurrentBotMessage('');
          setCurrentYAMLFixes(null);
          setIsLoading(false);
          setIsTyping(false);
          setThinkingSteps([]);
          setCanCancel(false);
          setCurrentMessageId(null);
          // Clean up the message handler on timeout
          webSocketClient.offMessage(handleResponse);
        }
      }, 300000); // 5 mins timeout (matches backend context timeout for large IaC queries)

    } catch (error) {
      console.error('Failed to send finding message:', error);
      setMessages((prev) => [...prev, {
        id: `error-${Date.now()}`,
        text: 'Sorry, I encountered an error. Please try again.',
        sender: 'bot',
        timestamp: Date.now(),
        thinking: (accumulatedThinking || accumulatedSteps.length > 0) ? {
          text: accumulatedThinking,
          steps: accumulatedSteps,
          isExpanded: false,
        } : undefined,
        yamlFixes: receivedYAMLFixes || undefined,
      }]);
      setIsLoading(false);
      setIsTyping(false);
      setThinkingSteps([]);
      setCurrentYAMLFixes(null);
      setCanCancel(false);
      setCurrentMessageId(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        return;
      } else {
        e.preventDefault();
        handleSend();
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    
    // Auto-resize textarea
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
  };

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-background to-muted/30">
      {/* Header */}
      <div className="p-4 border-b border-border/50">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="absolute inset-0 bg-primary/20 rounded-full blur-sm"></div>
            <Bot className="relative h-6 w-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-foreground">
              AI Remediation Assistant
            </h3>
            <div className="flex items-center gap-2 mt-0.5">
              {isConnected ? (
                <>
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                    Connected
                  </span>
                </>
              ) : (
                <>
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span className="text-xs text-red-600 dark:text-red-400 font-medium">
                    Disconnected
                  </span>
                </>
              )}
            </div>
          </div>
          {sessionManager.enabled && (
            <ChatHistoryDropdown
              sessions={sessionManager.chatHistory.sessions}
              currentSessionId={sessionManager.chatHistory.currentSessionId}
              loading={sessionManager.chatHistory.loading}
              showHistory={sessionManager.showHistory}
              setShowHistory={sessionManager.setShowHistory}
              historyRef={sessionManager.historyRef}
              onLoadSession={sessionManager.handleLoadSession}
              onNewChat={sessionManager.handleNewChat}
              formatSessionDate={sessionManager.formatSessionDate}
              disabled={isLoading || canCancel}
            />
          )}
        </div>
      </div>

      {/* Messages Area */}
      <div 
        ref={scrollAreaRef} 
        className="flex-1 overflow-y-auto px-4 py-4 space-y-4 scroll-smooth"
      >
        {connectionError && (
          <div className="animate-in slide-in-from-top-2 duration-300">
            <div className="flex items-center gap-3 p-3 bg-red-50/80 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl backdrop-blur-sm">
              <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700 dark:text-red-300 font-medium">{connectionError}</p>
            </div>
          </div>
        )}
        
        {messages.length === 0 && !isLoading && !connectionError ? (
          <div className="flex flex-col items-center justify-center text-center animate-in fade-in-50 duration-700 py-8">
            <div className="relative mb-4">
              <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl"></div>
              <div className="relative p-4 bg-gradient-to-br from-primary/10 to-primary/5 rounded-full border border-primary/20">
                <MessageCircle className="h-8 w-8 text-primary" />
              </div>
            </div>
            <h4 className="text-lg font-semibold text-foreground mb-2">
              Ask me about this vulnerability
            </h4>
            <p className="text-muted-foreground text-sm mb-6 max-w-sm leading-relaxed">
              I can help you understand how to fix this security finding and provide specific YAML configurations.
            </p>
            <div className="grid grid-cols-1 gap-2 w-full max-w-md">
              {getSamplePrompts().map((prompt, index) => (
                <button
                  key={prompt}
                  onClick={() => handleSamplePrompt(prompt)}
                  disabled={!isConnected}
                  className={cn(
                    "p-3 bg-card/50 backdrop-blur-sm border border-border/50 rounded-lg text-left",
                    "hover:bg-card/80 hover:border-border hover:shadow-md",
                    "transition-all duration-300 text-sm leading-relaxed",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                    "animate-in slide-in-from-top-4 duration-500"
                  )}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="flex items-start gap-2">
                    <MessageCircle className="h-3 w-3 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-xs">{prompt}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Historical messages */}
            {(() => {
              const completedMessages = isLoading && messages.length > 0 && messages[messages.length - 1].sender === 'user' 
                ? messages.slice(0, -1) 
                : messages;
              
              return completedMessages.map((message, index) => {
                if (message.sender === 'user') {
                  return (
                    <div key={message.id} className={cn(
                      "animate-in slide-in-from-bottom-2 duration-500 flex gap-3 justify-end"
                    )}>
                      <div className="max-w-[85%]">
                        <div className="rounded-2xl px-3 py-2 shadow-sm bg-primary text-primary-foreground">
                          <p className="text-sm leading-relaxed whitespace-pre-wrap">
                            {message.text}
                          </p>
                        </div>
                      </div>
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-blue-500/20 to-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                        <User className="h-3 w-3 text-blue-500" />
                      </div>
                    </div>
                  );
                } else {
                  return (
                    <div key={message.id} className={cn(
                      "animate-in slide-in-from-bottom-2 duration-500 flex gap-3 justify-start"
                    )}>
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 border border-primary/20 flex items-center justify-center">
                        <Bot className="h-3 w-3 text-primary" />
                      </div>
                      <div className="max-w-[85%] space-y-2">
                        {/* Thinking Display for Bot Messages */}
                        {message.thinking && (
                          <ThinkingDisplay 
                            thinking={message.thinking}
                            messageId={message.id}
                          />
                        )}
                        
                        {/* Message Bubble */}
                        <div className="rounded-2xl px-3 py-2 bg-card/80 backdrop-blur-sm border border-border/50 shadow-sm">
                          <div className="text-sm leading-relaxed break-words prose-chat">
                            <ChatMarkdown text={message.text} />
                          </div>
                          
                          {/* YAML Fixes Display */}
                          {message.yamlFixes && (
                            <YAMLFixesDisplay yamlFixes={message.yamlFixes} />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }
              });
            })()}

            {/* Active conversation */}
            {isLoading && (
              <div className="space-y-3">
                {/* Show the most recent user message if we're processing */}
                {messages.length > 0 && messages[messages.length - 1].sender === 'user' && (
                  <div className="animate-in slide-in-from-bottom-2 duration-300 flex gap-3 justify-end">
                    <div className="max-w-[85%]">
                      <div className="rounded-2xl px-3 py-2 shadow-sm bg-primary text-primary-foreground">
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">
                          {messages[messages.length - 1].text}
                        </p>
                      </div>
                    </div>
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-blue-500/20 to-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                      <User className="h-3 w-3 text-blue-500" />
                    </div>
                  </div>
                )}

                {/* Waiting Indicator - shown while waiting for backend to start */}
                {!isThinking && !isTyping && !currentBotMessage && (
                  <div className="animate-in slide-in-from-bottom-2 duration-300 flex gap-3 justify-start">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 border border-primary/20 flex items-center justify-center">
                      <Loader2 className="h-3 w-3 text-primary animate-spin" />
                    </div>
                    <div className="rounded-2xl px-3 py-2 bg-card/80 backdrop-blur-sm border border-border/50 shadow-sm">
                      <p className="text-xs text-muted-foreground">Starting analysis...</p>
                    </div>
                  </div>
                )}

                {/* Thinking Indicator */}
                {isThinking && (
                  <div className="animate-in slide-in-from-bottom-2 duration-300 flex gap-3 justify-start">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/30 flex items-center justify-center relative overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-cyan-400/20 animate-pulse" style={{animationDuration: '2s'}}></div>
                      <Brain className="h-3 w-3 text-blue-600 dark:text-blue-400 relative z-10 animate-pulse" />
                    </div>
                    <div className="max-w-[85%]">
                      <ThinkingDisplay
                        thinking={{ text: thinkingText, steps: thinkingSteps, isExpanded: isThinkingExpanded }}
                        messageId="streaming"
                        isStreaming={!thinkingComplete}
                      />
                    </div>
                  </div>
                )}

                {/* Typing Indicator */}
                {isTyping && !currentBotMessage && (
                  <div className="animate-in slide-in-from-bottom-2 duration-300 flex gap-3 justify-start">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 border border-primary/20 flex items-center justify-center">
                      <Bot className="h-3 w-3 text-primary" />
                    </div>
                    <div className="rounded-2xl px-3 py-2 bg-card/80 backdrop-blur-sm border border-border/50 shadow-sm">
                      <div className="flex items-center space-x-1">
                        <span className="h-1.5 w-1.5 bg-primary/60 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                        <span className="h-1.5 w-1.5 bg-primary/60 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                        <span className="h-1.5 w-1.5 bg-primary/60 rounded-full animate-bounce"></span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Streaming Bot Message */}
                {currentBotMessage && (
                  <div className="animate-in slide-in-from-bottom-2 duration-300 flex gap-3 justify-start">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 border border-primary/20 flex items-center justify-center">
                      <Bot className="h-3 w-3 text-primary" />
                    </div>
                    <div className="max-w-[85%] rounded-2xl px-3 py-2 bg-card/80 backdrop-blur-sm border border-border/50 shadow-sm">
                      <div className="text-sm leading-relaxed break-words prose-chat">
                        <ChatMarkdown text={currentBotMessage} />
                      </div>
                      
                      {/* Show current YAML fixes if available */}
                      {currentYAMLFixes && (
                        <YAMLFixesDisplay yamlFixes={currentYAMLFixes} />
                      )}

                      {waitingForFixes && (
                        <div className="flex items-center gap-2 mt-3 pt-2 border-t border-border/30">
                          <Loader2 className="h-3.5 w-3.5 text-muted-foreground animate-spin" />
                          <span className="text-xs text-muted-foreground">Loading configuration fixes</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Input Area - Moved to bottom with minimal padding */}
      <div className="border-t border-border/50 p-2">
        <div className="relative flex items-end gap-2 p-2 bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
          <div className="flex-1 min-h-[40px] max-h-24">
            <textarea
              ref={inputRef}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={isConnected ? "Ask about this vulnerability..." : "Connecting..."}
              className={cn(
                "w-full border-0 bg-transparent resize-none text-sm leading-relaxed",
                "focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none placeholder:text-muted-foreground/60",
                "min-h-[40px] max-h-24 py-2 px-3 overflow-y-auto"
              )}
              disabled={isLoading || !isConnected}
            />
          </div>
          
          <div className="flex-shrink-0 self-center">
            <Button
              onClick={canCancel ? handleCancel : handleSend}
              disabled={(!input.trim() && !canCancel) || !isConnected}
              size="icon"
              className={cn(
                "h-9 w-9 rounded-lg transition-all duration-200",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                canCancel 
                  ? "bg-red-600 hover:bg-red-700 text-white" 
                  : "bg-primary hover:bg-primary/90",
                (!input.trim() && !canCancel) || (!isConnected && !canCancel)
                  ? "scale-95 opacity-50" 
                  : "scale-100 hover:scale-105 shadow-lg"
              )}
              title={canCancel ? "Stop request" : "Send message"}
            >
              {canCancel ? (
                <X className="h-4 w-4" />
              ) : isLoading ? (
                <div className="h-3 w-3 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
              ) : (
                <Send className="h-3 w-3" />
              )}
            </Button>
          </div>
        </div>
        
        <p className="text-xs text-center text-muted-foreground/80 mt-1 px-1">
          AI can make mistakes. Please verify remediation steps before applying to production.
        </p>
      </div>
    </div>
  );
} 